<template>
  <div class="record-video-page">
    <!-- 主要内容 -->
    <main class="page-content">
      <!-- 拍摄注意事项 -->
      <div class="instructions">
        <h3>拍摄注意事项</h3>
        <ul>
          <li>请确保光线充足，避免背光，保持面部清晰，不要遮挡</li>
          <li>请保持稳定，避免画面晃动</li>
        </ul>
      </div>

      <!-- 视频预览区域 -->
      <div class="video-preview">
        <video ref="videoElement" autoplay muted></video>
        <canvas ref="canvasElement" style="display: none"></canvas>
      </div>

      <!-- 提交状态 -->
      <div v-if="isSubmitting" class="submitting-status">
        <div class="loading-spinner"></div>
        <span>正在提交视频...</span>
      </div>

      <!-- 提交结果 -->
      <div v-if="submitResult" class="submit-result"
:class="{ success: submitResult.success, error: !submitResult.success }">
        {{ submitResult.message }}
        <button v-if="submitResult.success" class="btn success small" @click="goToNext">继续</button>
      </div>
    </main>

    <!-- 控制按钮（固定在底部） -->
    <div class="fixed-controls">
      <!-- 录制状态 -->
      <div v-if="isRecording" class="recording-status">
        <span class="recording-indicator"></span>
        <span>正在录制...</span>
        <span class="countdown">剩余 {{ countdown }} 秒</span>
      </div>
      <!-- 摄像头可用时的操作 -->
      <div v-if="cameraAvailable" class="controls-content">
        <van-button class="info-btn" :disabled="isRecording" @click="switchCamera">{{ currentCamera === 'user' ? '切换后置'
          : '切换前置' }}</van-button>
        <van-button v-if="!isRecording" style="width: 100%" type="primary" class="primary-btn" color="#0AA8AF"
          :disabled="isSubmitting" @click="startRecording">开始录制</van-button>
        <van-button v-else style="width: 100%" type="success" class="primary-btn" color="#0AA8AF"
          :disabled="isSubmitting" @click="stopRecording">停止录制并提交</van-button>
      </div>
      <!-- 摄像头不可用时的上传视频入口 -->
      <div v-if="!cameraAvailable" class="recording-status">
        <span style="font-size: 14px; color: #2c3e50">摄像头不可用，您可以手动拍摄视频进行比对</span>
      </div>
      <div v-if="!cameraAvailable" class="controls-content">
        <van-button class="primary-btn" style="width: 100%" :disabled="isSubmitting" color="#0AA8AF"
          @click="uploadVideo">拍摄视频</van-button>
      </div>
    </div>
  </div>
</template>

<script>
import { livenessCompare } from '../../api/api'
import Vue from 'vue'
import { Toast, Dialog } from 'vant'

Vue.use(Toast)
Vue.use(Dialog)

export default {
  name: 'RecordVideo',
  data() {
    return {
      stream: null,
      mediaRecorder: null,
      recordedChunks: [],
      isRecording: false,
      recordedBlob: null,
      submitResult: null,
      countdown: 5,
      maxCountdown: 5,
      countdownTimer: null,
      isSubmitting: false,
      cameraAvailable: true,
      currentCamera: 'user' // 'user' 表示前置摄像头，'environment' 表示后置摄像头
    }
  },
  mounted() {
    // 不再自动初始化摄像头，而是等待用户点击开始录制按钮
    if (!this.stream) {
      Dialog.alert({
        title: '提示',
        message: '点击开始录制按钮录制视频'
      })
    }
  },
  beforeUnmount() {
    // 停止摄像头
    this.stopCamera()

    // 清除倒计时定时器
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
      this.countdownTimer = null
    }

    // 停止媒体录制器
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop()
    }
  },
  methods: {
    async initCamera() {
      try {
        // 检查浏览器兼容性
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('您的浏览器不支持摄像头访问功能，请使用现代浏览器')
        }

        // 请求摄像头权限并使用前置摄像头
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: this.currentCamera
          },
          audio: true
        })

        // 显示摄像头画面
        const videoElement = this.$refs.videoElement
        videoElement.srcObject = this.stream
        this.cameraAvailable = true
      } catch (error) {
        console.error('Error accessing camera:', error)
        let errorMessage = '无法访问摄像头，请检查权限设置'
        if (error.message.includes('不支持')) {
          errorMessage = error.message
        } else if (error.name === 'NotAllowedError') {
          errorMessage = '摄像头权限被拒绝，请在浏览器设置中允许摄像头访问'
        } else if (error.name === 'NotFoundError') {
          errorMessage = '未找到可用的摄像头设备'
        }
        this.cameraAvailable = false
        Toast(errorMessage)
      }
    },
    switchCamera() {
      if (this.isRecording || this.isSubmitting) {
        Toast('录制或提交时不允许切换摄像头')
        return // 录制或提交时不允许切换摄像头
      }
      // 切换摄像头方向
      this.currentCamera = this.currentCamera === 'user' ? 'environment' : 'user'
      // 停止当前摄像头
      this.stopCamera()
      // 显示切换摄像头的提示
      Toast('正在切换摄像头...')
      // 重新初始化摄像头
      this.initCamera()
    },

    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop())
        this.stream = null
      }
    },

    async startRecording() {
      try {
        // 首先初始化摄像头
        if (!this.stream) {
          await this.initCamera()
          // 如果初始化失败，stream 仍然为 null，直接返回
          if (!this.stream) return
        }

        // 重置倒计时
        this.countdown = this.maxCountdown

        // 创建媒体录制器
        this.mediaRecorder = new MediaRecorder(this.stream)
        this.recordedChunks = []
        this.isRecording = true

        // 录制数据处理
        this.mediaRecorder.ondataavailable = event => {
          if (event.data.size > 0) {
            this.recordedChunks.push(event.data)
          }
        }

        this.mediaRecorder.onstop = () => {
          // 生成录制的视频 blob
          this.recordedBlob = new Blob(this.recordedChunks, { type: 'video/mp4' })
          this.isRecording = false
        }

        // 开始录制
        this.mediaRecorder.start()
        setTimeout(() => {
          Toast('请保持稳定,开始倒计时自动提交')
          // 启动倒计时
          this.startCountdown()
        }, 2000)
      } catch (error) {
        console.error('Error starting recording:', error)
        Toast('无法开始录制，请检查摄像头权限')
      }
    },

    startCountdown() {
      // 清除之前的定时器
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
      }

      // 启动新的定时器
      this.countdownTimer = setInterval(() => {
        this.countdown--
        // 倒计时结束
        if (this.countdown <= 0) {
          clearInterval(this.countdownTimer)
          this.stopRecording()
        }
      }, 1000)
    },

    stopRecording() {
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop()
        Toast('录制结束，正在准备提交')
      }
      var time = this.maxCountdown - this.countdown
      if (time < 2) {
        Toast('请录制2秒以上视频')
        return
      }

      // 清除倒计时定时器
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }

      setTimeout(() => {
        console.log('准备提交视频')
        this.submitVideo()
      }, 200)
    },

    uploadVideo() {
      this.isSubmitting = true
      console.log('开始上传本地视频...')
      // 创建一个隐藏的文件输入元素
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'video/*'

      // 适配 iOS 设备
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
      if (isIOS) {
        // iOS 设备上使用 capture 属性可能会导致问题，使用不同的方式
        input.capture = 'camera'
      } else {
        // 非 iOS 设备直接调用摄像头
        input.capture = 'camera'
      }

      input.onchange = e => {
        const file = e.target.files[0]
        if (!file) {
          console.error('未选择视频')
          Toast('未选择视频')
          this.isSubmitting = false
          return
        }

        console.log('选择视频成功:', file)

        // 检查视频大小，限制为 20MB
        const maxSize = 20 * 1024 * 1024 // 20MB
        if (file.size > maxSize) {
          Toast('视频文件过大，请选择小于20MB的视频')
          this.isSubmitting = false
          return
        }

        // 直接使用选择的文件作为blob对象
        this.recordedBlob = file
        console.log('视频转换为blob成功:', this.recordedBlob)

        // 提交视频
        this.submitVideo()
      }

      // 触发文件选择
      input.click()
    },

    goToNext() {
      // 实际项目中替换为真实的路由
      this.$router.go(-1)
    },

    async submitVideo() {
      if (!this.recordedBlob) {
        Toast('请先录制视频')
        return
      }

      try {
        // 设置提交状态
        this.isSubmitting = true
        // 创建 FormData 对象
        const formData = new FormData()
        formData.append('video', this.recordedBlob, 'recording.mp4')
        formData.append('predict_id', this.$route.query.predict_id)
        formData.append('type', this.$route.query.type);
        console.log('formData', formData)

        Toast.loading({
          message: '正在提交视频...',
          duration: 0, // 持续展示 toast
          overlay: true,
          forbidClick: true,
          loadingType: 'spinner'
        })
        livenessCompare(formData)
          .then(res => {
            Toast.clear()
            console.log('res', res)
            this.isSubmitting = false
            if (res.data.result == 'report') {
              Dialog.alert({
                title: '提示',
                message: '人脸识别已达两次，请拍照上传。'
              }).then(() => {
                localStorage.setItem('alreadyTwice', '1')
                this.goToNext()
              })
            } else if (res.data.result == 'success') {
              Toast('视频提交成功！')
              this.goToNext()
            } else if (res.data.result == 'fail') {
              Dialog.alert({
                title: '提示',
                message: res.data.message
              })
            } else {
              Dialog.alert({
                title: '提示',
                message: '提交结果未知，请重试'
              })
            }
          })
          .catch(err => {
            Toast.clear()
            console.log('err', err)
            let errorMessage = '视频提交失败，请重试'
            if (err.response && err.response.data && err.response.data.message) {
              errorMessage = err.response.data.message
            } else if (err.message) {
              errorMessage = err.message
            }
            this.submitResult = {
              success: false,
              message: errorMessage
            }
            this.isSubmitting = false
            Toast(errorMessage)
          })
      } catch (error) {
        console.error('Error submitting video:', error)
        let errorMessage = '网络错误，请检查网络连接'
        if (error.message) {
          errorMessage = error.message
        }
        this.submitResult = {
          success: false,
          message: errorMessage
        }
        Toast(errorMessage)
        this.isSubmitting = false
      } finally {
        this.recordedBlob = null
        // 无论成功失败，都关闭提交状态
        Toast.clear()
      }
    }
  }
}
</script>

<style scoped>
.record-video-page {
  overflow-y: auto;
  min-height: 100vh;
  font-family: Arial, sans-serif;
  background-color: #f5f5f5;
}

/* 页面内容 */
.page-content {
  margin: 0 auto;
  padding: 20px;
  padding-bottom: 80px;
  max-width: 600px;
}

.instructions {
  margin-bottom: 14px;
  padding: 10px;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  background-color: #f8f9fa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.instructions:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.instructions h3 {
  display: flex;
  align-items: center;
  margin-top: 0;
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  gap: 8px;
}

.controls-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  padding: 5px;
  width: 100%;
  gap: 12px;

  .info-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 150px;
    height: 44px;
    flex: 0 0 auto;
    font-size: 14px;
    font-weight: 600;
  }

  .primary-btn {
    flex: 1 1 auto;
    height: 44px;
    font-size: 16px;
    font-weight: 600;
  }

  .success-btn {
    flex: 1 1 auto;
    height: 44px;
    font-size: 16px;
    font-weight: 600;
  }
}

.info-btn {
  padding: 0 15px;
  width: 150px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  color: white;
  background-color: #b8bab6;
  transition: all 0.3s ease;
}

.info-btn:hover {
  background-color: #a8aaa6;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.info-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.instructions h3::before {
  content: '💡';
  font-size: 20px;
}

.instructions ul {
  margin: 0;
  padding-left: 24px;
  list-style: none;
}

.instructions li {
  position: relative;
  margin-bottom: 12px;
  color: #495057;
  line-height: 1.1;
  /* padding-left: 20px; */
}

/* .instructions li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #4CAF50;
  font-size: 18px;
  font-weight: bold;
  line-height: 1.4;
} */

.instructions li:last-child {
  margin-bottom: 0;
}

.video-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  margin-bottom: 20px;
  width: 100%;
  height: 55vh;
  min-height: 550px;
  border-radius: 8px;
  background-color: #000;
}

.video-preview video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.controls {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  gap: 10px;
}

.btn {
  padding: 10px 20px;
  font-size: 14px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f0f0f0;
  transition: all 0.3s ease;
  cursor: pointer;
}

.btn:hover {
  background-color: #e0e0e0;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.primary:hover {
  background-color: #45a049;
}

.btn.danger {
  border-color: #f44336;
  color: white;
  background-color: #f44336;
}

.btn.danger:hover {
  background-color: #da190b;
}

.btn.success {
  border-color: #2196f3;
  background-color: #f196f3;
}

.btn.success:hover {
  background-color: #0b7dda;
}

.btn.small {
  margin-left: 10px;
  padding: 6px 12px;
  font-size: 12px;
}

/* 提交状态 */
.submitting-status {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  font-weight: bold;
  color: #2196f3;
  gap: 10px;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #2196f3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.recording-status {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  font-weight: bold;
  color: #f44336;
  gap: 10px;
}

.recording-status .countdown {
  padding: 5px 5px;
  font-size: 18px;
  font-weight: bold;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #f44336;
  background-color: rgba(244, 67, 54, 0.1);
}

.recording-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #f44336;
  animation: blink 1s infinite;
}

@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }
}

.submit-result {
  margin-top: 10px;
  padding: 10px;
  font-weight: bold;
  border-radius: 4px;
}

.submit-result.success {
  border: 1px solid #c3e6cb;
  color: #155724;
  background-color: #d4edda;
}

.submit-result.error {
  border: 1px solid #f5c6cb;
  color: #721c24;
  background-color: #f8d7da;
}

.fixed-controls {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 90;
  padding: 16px 20px;
  background-color: white;
  box-shadow: 0 -2px 10px rgba(255, 240, 240, 0.1);
}

@media (max-width: 768px) {
  .video-preview {
    height: 65vh;
    min-height: 400px;
  }

  .btn {
    width: 100%;
  }
}
</style>